/**
 * Sentry init (web) — F-014 Phase 4 (tasks 4.1–4.2).
 *
 * Import side-effect only: `import './sentry'` as the first statement in
 * `main.tsx`, before the React root is rendered. Init is conditional on
 * `VITE_SENTRY_DSN` so dev builds without the env var simply skip Sentry
 * (the SDK becomes a no-op).
 *
 * BrowserTracing is enabled with a 10% trace sample rate in production
 * and 0% elsewhere (keeps local dev quiet). Replay is opt-in via
 * `VITE_SENTRY_REPLAY_SESSION_RATE`; the error replay rate is always
 * 100% so any crash in prod is inspectable.
 *
 * EU region: when `VITE_SENTRY_DSN` targets `*.ingest.de.sentry.io` the
 * SDK auto-routes to the EU datacenter.
 */
import * as Sentry from '@sentry/react';

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const MODE = import.meta.env.MODE;
const RELEASE = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;
const IS_PROD = MODE === 'production';

if (DSN) {
  Sentry.init({
    dsn: DSN,
    environment: MODE,
    release: RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: IS_PROD ? 0.1 : 0,
    // Use tunnel route so ad-blockers (which often target sentry.io) don't
    // drop events. The backend forwards the payload to Sentry ingest.
    tunnel: '/api/sentry-tunnel',
    replaysSessionSampleRate: IS_PROD
      ? Number(import.meta.env.VITE_SENTRY_REPLAY_SESSION_RATE ?? '0.01')
      : 0,
    replaysOnErrorSampleRate: 1.0,
  });
} else if (IS_PROD) {
  // Production builds SHOULD have the DSN. Surface a console.warn but
  // do not throw — a missing DSN must not take the app down.
  // eslint-disable-next-line no-console
  console.warn('[sentry] VITE_SENTRY_DSN not set — error tracking disabled in production build');
}

export { Sentry };
